import React, { FC, useEffect, useRef } from 'react';
import { Tooltip, TooltipActions, TooltipProps } from '../Tooltip';
import {
  ListItemSelectIconBuilderParams,
  ListItemSelectIconProps,
} from './ListItemSelectIcon.types';
import { classes, st } from './ListItemSelectIcon.st.css';

const defaultTooltipProps: Omit<TooltipProps, 'children'> = {
  placement: 'right',
  flip: true,
};

const ListItemSelectIcon: FC<ListItemSelectIconProps> = ({
  highlighted = false,
  selected = false,
  disabled = false,
  dataHook = 'list-item-select-icon',
  icon,
  label,
  className,
  showTooltip,
  tooltipProps,
  onClick,
}) => {
  const tooltipRef = useRef<TooltipActions>(null);

  useEffect(() => {
    const tooltipActions = tooltipRef.current;

    if (!tooltipActions || !showTooltip) {
      return;
    }

    if (highlighted) {
      tooltipActions.open();
    } else {
      tooltipActions.close();
    }

    return () => {
      tooltipActions.close();
    };
  }, [highlighted, showTooltip]);

  let content = <div className={classes.content}>{icon}</div>;

  if (showTooltip) {
    const mergedTooltipProps = {
      ...defaultTooltipProps,
      ...tooltipProps,
      disabled,
      content: label,
      className: classes.contentWrapper,
    };

    content = (
      <Tooltip ref={tooltipRef} {...mergedTooltipProps}>
        {content}
      </Tooltip>
    );
  }

  return (
    <div
      className={st(
        classes.root,
        { highlighted, selected, disabled },
        className,
      )}
      aria-label={label}
      data-hook={dataHook}
      data-selected={selected}
      onClick={disabled ? undefined : onClick}
    >
      {content}
    </div>
  );
};

ListItemSelectIcon.displayName = 'ListItemSelectIcon';

export { ListItemSelectIcon };
type BuilderValueParams = {
  selected?: boolean;
  hovered?: boolean;
  disabled?: boolean;
};

export const listItemSelectIconBuilder = ({
  id,
  className,
  icon,
  label,
  disabled,
  dataHook,
  showTooltip,
  tooltipProps,
}: ListItemSelectIconBuilderParams) => ({
  id,
  icon,
  disabled,
  overrideOptionStyle: true,
  label,
  value: ({ hovered, ...rest }: BuilderValueParams) => (
    <ListItemSelectIcon
      icon={icon}
      label={label}
      highlighted={hovered}
      dataHook={dataHook}
      className={className}
      showTooltip={showTooltip}
      tooltipProps={tooltipProps}
      {...rest}
    />
  ),
});
